/**
 * PATH: packages/ui/src/kanban/components/settings-home/useKanbanChromePrefs.ts
 * FILE_ID: UI-KANBAN-HOOK-CHROME-PREFS-001
 * PURPOSE: Durable card density + keyboard mode via kanban:board-view.
 * ROLE IN ARCHITECTURE: Settings-hub / All-Kanban chrome persistence.
 * MAIN EXPORTS: useKanbanChromePrefs
 * NON-RESPONSIBILITIES: Does not own inbox rail, filters, or right-rail width.
 * NOTES FOR FUTURE AI: Merge into the existing board-view blob. Do not wipe it.
 */

import { useEffect, useRef, useState } from 'react'
import {
  fetchServerState,
  scheduleServerSave,
} from '@quotech/tenant-shared/preferences/dbViewPreference'

import type { CardDensity } from '../board-view/cardDensity'

const BOARD_VIEW_DB_SCOPE = 'kanban:board-view'

type BoardViewChrome = {
  cardDensity?: CardDensity
  keyboardMode?: boolean
  [key: string]: unknown
}

function isDensity(value: unknown): value is CardDensity {
  return value === 'compact' || value === 'expanded' || value === 'large'
}

/** Hydrate and persist density + keyboard without dropping sibling chrome keys. */
export function useKanbanChromePrefs(): {
  cardDensity: CardDensity
  setCardDensity: (next: CardDensity) => void
  keyboardMode: boolean
  setKeyboardMode: (next: boolean) => void
} {
  const [cardDensity, setCardDensityState] = useState<CardDensity>('expanded')
  const [keyboardMode, setKeyboardModeState] = useState(false)
  const hydratedRef = useRef(false)
  const blobRef = useRef<BoardViewChrome>({})

  useEffect(() => {
    let cancelled = false
    void fetchServerState<BoardViewChrome>(BOARD_VIEW_DB_SCOPE, {}).then((state) => {
      if (cancelled) return
      if (state) {
        blobRef.current = { ...state }
        if (isDensity(state.cardDensity)) setCardDensityState(state.cardDensity)
        if (typeof state.keyboardMode === 'boolean') setKeyboardModeState(state.keyboardMode)
      }
      hydratedRef.current = true
    })
    return () => {
      cancelled = true
    }
  }, [])

  const persist = (patch: Pick<BoardViewChrome, 'cardDensity' | 'keyboardMode'>) => {
    blobRef.current = { ...blobRef.current, ...patch }
    if (!hydratedRef.current) return
    scheduleServerSave(BOARD_VIEW_DB_SCOPE, blobRef.current)
  }

  return {
    cardDensity,
    setCardDensity: (next) => {
      setCardDensityState(next)
      persist({ cardDensity: next })
    },
    keyboardMode,
    setKeyboardMode: (next) => {
      setKeyboardModeState(next)
      persist({ keyboardMode: next })
    },
  }
}
